<template>
  <div class="rounded-l border py-8">
    <div class="mb-4 flex flex-col items-center gap-3">
      <Lock class="mb-3 h-8 w-8" />
      <h1 class="mb-4 text-center text-base font-light leading-5">
        {{ t('passwordProtect.title') }}
      </h1>
    </div>

    <form @submit.prevent="onSubmit" class="form-wrapper">
      <PinInputWrapped
        v-model="password"
        v-bind="passwordAttrs"
        name="accessCode"
        :label="t('input.label.accessCode')"
        :error="errors.password"
        :password-length="passwordLength"
      />
      <Button type="submit"> {{ t('common.submit') }} </Button>
      <InfoBar
        v-if="error"
        variant="destructive"
        :title="t('common.error')"
        :message="error"
        @close="error = ''"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useForm } from 'vee-validate';
import { useCookies } from '@vueuse/integrations/useCookies';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';

// Components
import InfoBar from '@generics/InfoBar.vue';
import Button from '@ui/button/Button.vue';
import { useI18n } from 'vue-i18n';

// Icons
import { Lock } from 'lucide-vue-next';
import PinInputWrapped from '@modules/PinInputWrapped/PinInputWrapped.vue';

const { t } = useI18n();

interface Form {
  password: string;
}

const passwordLength = import.meta.env.VITE_FF_PASSWORD?.length || 5;

const cookies = useCookies(['stored-password']);

const schema = toTypedSchema(
  yup.object({
    password: yup.string().required(t('errors.required')),
  }),
);

const { errors, handleSubmit, defineField, values } = useForm<Form>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    password: '',
  },
});

const [password, passwordAttrs] = defineField('password', {
  validateOnModelUpdate: false,
  validateOnBlur: false,
  validateOnChange: false,
});

const error = ref('');

const onSubmit = handleSubmit((values) => {
  if (values.password === import.meta.env.VITE_FF_PASSWORD) {
    cookies.set('stored-password', values.password, {
      maxAge: 60 * 60 * 24 * 30,
      path: '/',
    });
  } else {
    if (cookies.get('stored-password')) {
      cookies.remove('stored-password');
    }
    password.value = '';
    nextTick(() => {
      error.value = t('errors.incorrectAccessCode');
    });
  }
});

watch(values, () => (error.value = ''), { deep: true });
</script>
